/**
 * Notation Service
 * 
 * A service for converting between different chess notation formats 
 * and providing helpful hints for learning notation.
 */

import { Chess, Move, Square } from 'chess.js';
import { ChessMove, PieceType, PieceColor, FEN } from '../../types/chess';
import { resolveMissionMoveSan } from './missionMoveSan';

/**
 * Service for handling chess notation
 */
class NotationService {
  private game: Chess;
  private silent: boolean;

  /**
   * Create a new NotationService instance
   * @param fen Optional FEN string to set initial position
   * @param silent Whether to suppress error logging (useful for testing)
   */
  constructor(fen?: string, silent: boolean = process.env.NODE_ENV === 'test') {
    this.game = new Chess(fen);
    this.silent = silent;
  }

  /**
   * Convert Standard Algebraic Notation (SAN) to a move object
   * @param san Move in SAN format (e.g., "Nf3", "e4", "O-O")
   * @returns Move object or null if the move is invalid
   */
  sanToMove(san: string): ChessMove | null {
    try {
      // Make the move on a temporary board to get the move details
      const tempGame = new Chess(this.game.fen());
      const moveResult = tempGame.move(san);
      
      if (!moveResult) return null;
      
      return this.convertMoveFormat(moveResult);
    } catch (error) {
      if (!this.silent) {
        console.error(`Invalid SAN move: ${san}`, error);
      }
      return null;
    }
  }

  /**
   * Convert a move (from/to squares) to Standard Algebraic Notation
   * @param from Starting square
   * @param to Destination square
   * @param promotion Optional promotion piece
   * @returns Move in SAN format or null if invalid
   */
  moveToSan(from: string, to: string, promotion?: PieceType): string | null {
    try {
      // Make the move on a temporary board to get the SAN
      const tempGame = new Chess(this.game.fen());
      const moveResult = tempGame.move({
        from: from as Square,
        to: to as Square,
        promotion
      });
      
      if (!moveResult) return null;
      
      return moveResult.san;
    } catch (error) {
      if (!this.silent) {
        console.error(`Error converting move to SAN: ${from}-${to}`, error);
      }
      return null;
    }
  }

  /**
   * Generate a hint for how to read/interpret a SAN move
   * @param san Move in SAN format
   * @returns Detailed explanation of the move or null if invalid
   */
  generateHint(san: string, fen?: FEN): string | null {
    try {
      // Handle castling
      if (san === 'O-O') {
        return 'Kingside castling: The king moves two squares toward the h-file rook, and the rook moves to the square the king crossed.';
      } else if (san === 'O-O-O') {
        return 'Queenside castling: The king moves two squares toward the a-file rook, and the rook moves to the square the king crossed.';
      }
      
      const resolvedMove = fen ? resolveMissionMoveSan(fen, san) : null;
      const effectiveSan = resolvedMove?.canonicalSan ?? san;
      const move = resolvedMove?.move ?? this.sanToMove(effectiveSan);
      if (!move) return null;
      
      let hint = '';
            
      // Parse the move
      const piece = this.getPieceName(move.piece);
      const isCapture = effectiveSan.includes('x') || move.flags.includes('c');
      const isCheck = resolvedMove ? resolvedMove.isCheck : effectiveSan.includes('+');
      const isCheckmate = resolvedMove ? resolvedMove.isCheckmate : effectiveSan.includes('#');
      const isPromotion = effectiveSan.includes('=');
      
      // Start building the hint
      if (move.piece === 'p') {
        hint = `Pawn move from ${move.from} to ${move.to}`;
      } else {
        hint = `${piece} move from ${move.from} to ${move.to}`;
      }
      
      // Add capture information
      if (isCapture) {
        hint += `, capturing a piece`;
      }
      
      // Add promotion information
      if (isPromotion) {
        const promotionPiece = this.getPieceName(move.san.slice(move.san.indexOf('=') + 1) as PieceType);
        hint += `, promoting to a ${promotionPiece}`;
      }
      
      // Add check/checkmate information
      if (isCheckmate) {
        hint += `, resulting in checkmate`;
      } else if (isCheck) {
        hint += `, putting the opponent's king in check`;
      }
      
      return hint;
    } catch (error) {
      if (!this.silent) {
        console.error(`Error generating hint for ${san}`, error);
      }
      return null;
    }
  }

  /**
   * Set the board position
   * @param fen FEN string representing the board position
   * @returns True if position was set successfully
   */
  setPosition(fen: FEN): boolean {
    try {
      const result = this.game.load(fen);
      return true; // chess.js returns void on success and throws on error
    } catch (error) {
      if (!this.silent) {
        console.error(`Error setting position: ${fen}`, error);
      }
      return false;
    }
  }

  /**
   * Get the current FEN position
   * @returns FEN string
   */
  getPosition(): FEN {
    return this.game.fen();
  }

  /**
   * Decode FEN to a human-readable description
   * @param fen FEN string to decode
   * @returns Human-readable description of the position
   */
  decodeFen(fen: FEN): string {
    try {
      const parts = fen.split(' ');
      if (parts.length !== 6) {
        return 'Invalid FEN string';
      }
      
      const [board, turn, castling, enPassant, halfmove, fullmove] = parts;
      
      let description = 'Position with ';
      
      // Add turn information
      description += turn === 'w' ? 'White to move. ' : 'Black to move. ';
      
      // Add castling information
      if (castling === '-') {
        description += 'No castling rights available. ';
      } else {
        let castlingRights = [];
        if (castling.includes('K')) castlingRights.push('White kingside');
        if (castling.includes('Q')) castlingRights.push('White queenside');
        if (castling.includes('k')) castlingRights.push('Black kingside');
        if (castling.includes('q')) castlingRights.push('Black queenside');
        description += `Castling available: ${castlingRights.join(', ')}. `;
      }
      
      // Add en passant information
      if (enPassant !== '-') {
        description += `En passant possible on ${enPassant}. `;
      }
      
      // Add move counts
      description += `Halfmove clock: ${halfmove}. Fullmove number: ${fullmove}.`;
      
      return description;
    } catch (error) {
      if (!this.silent) {
        console.error(`Error decoding FEN: ${fen}`, error);
      }
      return 'Invalid FEN string';
    }
  }

  /**
   * Convert chess.js move format to our ChessMove format
   * @param move The chess.js move object
   * @returns Our ChessMove format
   */
  private convertMoveFormat(move: Move): ChessMove {
    return {
      from: move.from,
      to: move.to,
      promotion: move.promotion as PieceType | undefined,
      piece: move.piece as PieceType,
      color: move.color as PieceColor,
      flags: move.flags,
      san: move.san
    };
  }

  /**
   * Get the full name of a piece from its symbol
   * @param piece Piece type symbol
   * @returns Full name of the piece
   */
  private getPieceName(piece: PieceType): string {
    switch (piece) {
      case 'p': return 'Pawn';
      case 'n': return 'Knight';
      case 'b': return 'Bishop';
      case 'r': return 'Rook';
      case 'q': return 'Queen';
      case 'k': return 'King';
      default: return 'Unknown piece';
    }
  }
}

export default NotationService; 
